import React from 'react';
import {
  AppWindow,
  PhoneCall,
  MapPinOff,
  Frown,
  Smartphone,
  Bell,
  Sparkles,
  Radio,
  MessageCircle,
  Building2,
  CloudUpload,
  FileText,
  Send
} from 'lucide-react';

import LeisureImg from '../../assets/App_Img/landing_leisure_travel.png';
import GroupImg from '../../assets/App_Img/landing_group_tour.png';
import CorporateImg from '../../assets/App_Img/landing_corporate_mice.png';
import DmcImg from '../../assets/App_Img/landing_dmc_specialist.png';

import TajMahalIcon from '../../assets/App_Img/taj_mahal_icon.png';
import GlobeIcon from '../../assets/App_Img/globe_icon.png';
import RestaurantIcon from '../../assets/App_Img/restaurant_icon.png';
import ActivityIcon from '../../assets/App_Img/activity_icon.png';
import ShopIcon from '../../assets/App_Img/shop_icon.png';

// WhyTrrip Section Data
export const painPoints = [
  {
    icon: <AppWindow size={32} strokeWidth={1.5} className="text-rose-500" />,
    title: 'Your clients juggle five different apps',
    desc: 'Hotel emails, flight tickets, WhatsApp messages, Google Maps, and random blog posts- Nothing has your name on it',
    bg: 'from-rose-50 to-rose-100/50',
    blob: 'bg-rose-100'
  },
  {
    icon: <PhoneCall size={32} strokeWidth={1.5} className="text-rose-500" />,
    title: "You're on call for things you can't fix",
    desc: 'Flight delays, unexpected restaurant closures, or bad directions. You get the midnight panic calls - without the tools to fix it.',
    bg: 'from-rose-50 to-rose-100/50',
    blob: 'bg-rose-100'
  },
  {
    icon: <MapPinOff size={32} strokeWidth={1.5} className="text-rose-500" />,
    title: 'They miss out on what makes the trip special',
    desc: 'Without your guidance, travellers rely on Google searches, hotel concierges, or whatever local tout pushes.',
    bg: 'from-rose-50 to-rose-100/50',
    blob: 'bg-rose-100'
  },
  {
    icon: <Frown size={32} strokeWidth={1.5} className="text-rose-500" />,
    title: 'Your brand fades between bookings',
    desc: 'Zero post-trip engagement means you lose top-of-mind recall. By their next vacation, they’re already price-shopping with competitors.',
    bg: 'from-rose-50 to-rose-100/50',
    blob: 'bg-rose-100'
  }
];

export const withTrripPoints = [
  {
    icon: <Smartphone size={32} strokeWidth={1.5} className="text-[#067978]" />,
    title: 'One white-labelled app for everything',
    desc: 'Itinerary, dining, activities, live updates — all in one place. All carrying your agency name and brand.',
    bg: 'from-[#e6f3ec] to-[#cfe6da]/50',
    blob: 'bg-[#cfe6da]'
  },
  {
    icon: <Bell size={32} strokeWidth={1.5} className="text-[#067978]" />,
    title: 'Live answers without your phone ringing',
    desc: 'Real-time flight tracking, transport status, safety alerts — built into the app. Your clients self-serve.',
    bg: 'from-[#e6f3ec] to-[#cfe6da]/50',
    blob: 'bg-[#cfe6da]'
  },
  {
    icon: <Sparkles size={32} strokeWidth={1.5} className="text-[#067978]" />,
    title: "Curated experiences they couldn't find alone",
    desc: 'Dining, activities, shopping — handpicked across 100+ destinations and ready to share with every booking.',
    bg: 'from-[#e6f3ec] to-[#cfe6da]/50',
    blob: 'bg-[#cfe6da]'
  },
  {
    icon: <Radio size={32} strokeWidth={1.5} className="text-[#067978]" />,
    title: 'You stay present through every moment',
    desc: 'Your brand shows up at every interaction — from arrival to departure. Repeat business gets easier, not harder.',
    bg: 'from-[#e6f3ec] to-[#cfe6da]/50',
    blob: 'bg-[#cfe6da]'
  }
];

// ModernTravel Section Data
export const modernTravelTabs = [
  {
    title: 'Leisure Travel Agents',
    desc: 'Perfect for FIT, family, and custom holiday planners. Give your clients a seamless, app-like experience for their vacations without writing a single line of code.',
    img: LeisureImg
  },
  {
    title: 'Group Tour Operators',
    desc: 'Manage group itineraries and updates effortlessly. Send real-time push notifications to all 50+ members of a group tour instantly when gate changes happen.',
    img: GroupImg
  },
  {
    title: 'MICE / Corporate Travel',
    desc: 'Share seamless itineraries for business travellers. Keep executives organized with precise timing, meeting locations, and flight details all in one place.',
    img: CorporateImg
  },
  {
    title: 'DMCs / Destination Specialists',
    desc: 'Promote local experiences and drive more value. Showcase your exclusive destination knowledge, local restaurant hookups, and hidden gems.',
    img: DmcImg
  }
];

// LiveTracking Section Data
export const liveTrackingStats = [
  { count: 40, suffix: '+', label: 'Domestic Cities', icon: TajMahalIcon },
  { count: 25, suffix: '+', label: 'International Cities', icon: GlobeIcon },
  { count: 500, suffix: '+', label: 'Restaurants', icon: RestaurantIcon },
  { count: 250, suffix: '+', label: 'Activity Providers', icon: ActivityIcon },
  { count: 200, suffix: '+', label: 'Shops', icon: ShopIcon }
];

// StartUsing Section Data
export const startUsingSteps = [
  {
    num: 1,
    text: 'Sign up with your WhatsApp number',
    icon: <MessageCircle size={28} strokeWidth={1.5} className="text-[#067978]" />
  },
  {
    num: 2,
    text: 'Complete company profile',
    icon: <Building2 size={28} strokeWidth={1.5} className="text-[#067978]" />
  },
  {
    num: 3,
    text: 'Upload first booking',
    icon: <CloudUpload size={28} strokeWidth={1.5} className="text-[#067978]" />
  },
  {
    num: 4,
    text: 'Create voucher',
    icon: <FileText size={28} strokeWidth={1.5} className="text-[#067978]" />
  },
  {
    num: 5,
    text: 'Share with the traveller',
    icon: <Send size={28} strokeWidth={1.5} className="text-[#067978]" />
  }
];

// Faq Section Data
export const faqsData = [
  {
    question: 'Is Trrip a booking engine?',
    answer:
      'No, Trrip is a post-booking platform designed to help you create master itineraries, share digital vouchers, and track traveller progress seamlessly.'
  },
  {
    question: 'Will this change my current booking process?',
    answer:
      'Not at all. You can continue booking through your existing channels. Trrip simply helps you organize and present those bookings professionally.'
  },
  {
    question: 'What does the traveller receive?',
    answer:
      'Travellers receive a beautiful, interactive mobile web app containing their full itinerary, all digital vouchers, and personalized local recommendations.'
  },
  {
    question: 'Do I need to install anything?',
    answer:
      'No installation is required. Trrip is a completely cloud-based platform accessible from any modern web browser.'
  },
  {
    question: 'Can I get help creating my first voucher?',
    answer:
      'Absolutely! We offer dedicated onboarding support and a quick 5-minute demo to walk you through creating your first master voucher.'
  }
];
